import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import isEqual from "lodash/isEqual";
import merge from "lodash/merge";
import { format } from "date-fns";
import {
    DEFAULT_AXNUI_CONTENT,
    DEFAULT_AXNUI_LAYOUT,
    DEFAULT_AXNUI_THEME,
    DEFAULT_DCUI_CONTENT,
    DEFAULT_DCUI_THEME
} from "@iddataweb/idw-common-ui/constants";
import { AttributeTypes, LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { resources } from "@constants/hardcoded-strings";
import { WORKFLOWS_URL } from "@constants/routes";
import {
    AXNUI_CONTENT_PROPERTY_TYPE_ID,
    AXNUI_LAYOUT_PROPERTY_TYPE_ID,
    AXNUI_THEME_PROPERTY_TYPE_ID,
    DCUI_CONTENT_PROPERTY_TYPE_ID,
    DCUI_THEME_PROPERTY_TYPE_ID,
    EMAIL_TEMPLATE_FIELDS_PROPERTY_TYPE_ID,
    INCODE_BIOGOVID_VERIFICATION_SERVICE_OFFERING_ID,
    USE_THEMED_UI_PROPERTY_TYPE_ID
} from "@constants/verification-service-details-constants";
import { SERVER_TIME_FORMAT } from "@constants/date-formats";
import { AppliedProfileTypes } from "@enums/applied-profile-types";
import { ButtonTypes } from "@enums/button-types";
import { ModalTypes } from "@enums/modal-types";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { useDiscardChangesModal } from "@hooks/use-discard-changes-modal";
import {
    AXNUIContent,
    AXNUIContentForm,
    AXNUILayout,
    AXNUITheme,
    DCUITheme
} from "@iddataweb/idw-common-ui/types";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { DISPLAY_RESULTS_PROPERTY_TYPE_ID } from "@constants/landing-page-constants";
import { formatMessagingTemplates } from "@formatters/messaging-templates/messaging-template-formatter";
import { FormattedIdwProfile } from "@idw-types/data/idw-profile";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import { AxnAttributeTypeField, AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { FormattedAdvancedWorkflowDetailsConfiguration, FormattedWorkflowDetailsConfiguration } from "@idw-types/data/workflow-details-configuration";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn/axn-documentation-link";
import { DCUIThemingStep } from "@idw-types/common/dcui/dcui-theming-step";
import {
    AttributeLabel,
    RpServiceTemplate
} from "@idw-types/data/reference-data/generic";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { WorkflowChangeRequest } from "@idw-types/data/workflow-change-request";
import { uploadResourceRequest } from "@requests/service-configuration/upload-resource";
import { FormattedMessagingTemplate, MessagingTemplateCache } from "@idw-types/data/messaging-template";
import { EmailTemplateFields } from "@idw-types/common/email-template-fields";
import { getProfilePropertyValuesForPropertyTypeId, getPropertyValuesPassedToService } from "@utils/applied-profile-util";
import { logAndDisplayErrorModal } from "@utils/app-alert-util";
import { convertFileToBase64 } from "@utils/file-util";
import { navigateToUtil } from "@utils/navigation-util";
import { getWorkflowValidationErrors } from "@utils/validation/workflow/workflow-validation-util";
import { getActiveWorkflowDCUICaptureSteps } from "@utils/dcui-capture-step-util";
import { getAxnAttributeTypeFieldMapWithoutSelfServiceCheck } from "@utils/self-service/self-service-util";
import { getAXNUIFormContentKey } from "@utils/attribute-input-util";
import { setValueAndAddedToProfileForProfileProperty } from "@utils/login-hint/login-hint-utils";
import {
    extractContentValuesFromProfileProperties,
    generateContentProfileProperty,
    getParsedContentPropertyValue
} from "@utils/content-util";
import {
    getAvailableMessagingTemplateTypesAndEmailTemplateFieldsFromRpServiceConfig,
    getCleanedEmailTemplateFields,
    getEmailTemplateFieldsJSONValueFromMessagingTemplates,
    getSelectedMessagingTemplates,
    updateOrCreateMessagingTemplates
} from "@utils/messaging-template/messaging-template-util";
import {
    getAppliedProfileRedirectURLs,
    getFormattedRpServiceConfigurationPropertyRedirectURLs,
    getRedirectUrlsInformation
} from "@utils/redirect-urls/redirect-urls-util";
import { getInheritedAndMergedJSONPropertyValue } from "@utils/verification-service-details-util";
import { TabContainer } from "@containers/common/tab-container/tab-container";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { WorkflowDetailsStartChangeRequestButton } from "@containers/workflow-details/workflow-details-component/workflow-details-start-change-request-button/workflow-details-start-change-request-button";
import { WorkflowDetailsGeneralTab } from "./workflow-details-general-tab/workflow-details-general-tab";
import { WorkflowDetailsThemingTab } from "./workflow-details-theming-tab/workflow-details-theming-tab";
import { WorkflowDetailsDCUIThemingTab } from "./workflow-details-dcui-theming-tab/workflow-details-dcui-theming-tab";
import { WorkflowDetailsAttributesTab } from "./workflow-details-attributes-tab/workflow-details-attributes-tab";
import { WorkflowDetailsVisualizationTab } from "./workflow-details-visualization-tab/workflow-details-visualization-tab";
import { WorkflowDetailsDropdownMenu } from "./workflow-details-dropdown-menu/workflow-details-dropdown-menu";
import styles from "./workflow-details-component.less";
import { AxnAdminUserPermissions } from "@idw-types/data/axn-admin-user";
import { CreateWorkflowVerificationServiceResponse } from "@requests/service-configuration/create-workflow-verification-service";
import { BaseRedirectUrl } from "@idw-types/redirect-url-information";
import { RedirectUrlOriginTypes } from "@enums/redirect-url-origin-types";
import { getCustomLanguages, updateRpServicesAXNUIAndDCUIContentProperty } from "@utils/language-selector-util";
import { PageLevelTypes } from "@enums/page-level-types";
import { setPartialCommonReferenceData } from "@redux/actions/common-reference-data-actions";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { getUpdatedAxnRpServices } from "@utils/workflow-util";
import { SimpleRPServiceWorkflow } from "@idw-types/data/reference-data/axn/simple-rp-service-workflow";

type WorkflowDetailsComponentProps = {
    selectedLanguage: LocaleTypes;
    availableLanguages: LocaleTypes[];
    setSelectedLanguage: (selectedLanguage: LocaleTypes) => void;
    setAvailableLanguages: (availableLanguages: Array<LocaleTypes>) => void;
    axnRelyingParties: Array<AxnRelyingParty>;
    axnCompanies: Array<AxnCompany>;
    initialWorkflowDetailsConfiguration: FormattedWorkflowDetailsConfiguration;
    initialRpServiceConfigurations: Array<FormattedRpServiceConfiguration>;
    axnDocumentationLinks: Array<AxnDocumentationLink>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    attributeLabels: Array<AttributeLabel>;
    workflowChangeRequests: Array<WorkflowChangeRequest>;
    rpServiceTemplates: Array<RpServiceTemplate>;
    axnRPServices: Array<AxnRpService>;
    axnAdminRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    axnRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    userPermissions: AxnAdminUserPermissions;
    shouldRefreshComponentState: boolean;
    setWorkflowChangeRequest: (changeRequest: WorkflowChangeRequest) => void;
    handleSave: (
        workflowDetailsConfiguration: FormattedWorkflowDetailsConfiguration,
        rpServiceConfigurations: Array<FormattedRpServiceConfiguration>
    ) => Promise<FormattedAdvancedWorkflowDetailsConfiguration>;
    getFormattedRpServiceForNewService: (data: CreateWorkflowVerificationServiceResponse) => FormattedRpServiceConfiguration;
};

export const WorkflowDetailsComponent = (props: WorkflowDetailsComponentProps) => {
    const {
        selectedLanguage,
        availableLanguages,
        setSelectedLanguage,
        setAvailableLanguages,
        axnRelyingParties,
        initialWorkflowDetailsConfiguration,
        shouldRefreshComponentState,
        initialRpServiceConfigurations,
        axnCompanies,
        axnDocumentationLinks,
        axnAttributeTypeFields,
        attributeLabels,
        workflowChangeRequests,
        rpServiceTemplates,
        axnRPServices,
        userPermissions,
        axnAdminRPServiceWorkflows,
        axnRPServiceWorkflows,
        handleSave,
        setWorkflowChangeRequest,
        getFormattedRpServiceForNewService
    } = props;

    const navigate = useNavigate();
    const routerLocation = useLocation();
    const [workflowDetailsConfigurationState, setWorkflowDetailsConfigurationState] = useState<FormattedWorkflowDetailsConfiguration>(initialWorkflowDetailsConfiguration);
    const [rpServiceConfigurationsState, setRpServiceConfigurationsState] = useState<Array<FormattedRpServiceConfiguration>>(initialRpServiceConfigurations);
    const [workflowMessagingTemplateCache, setWorkflowMessagingTemplateCache] = useState<MessagingTemplateCache>({});
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [isMultiLanguageEnabled, setIsMultiLanguageEnabled] = useState(false);

    const dispatch = useDispatch();
    const stateIsUnchanged = useMemo(() => {
        return (
            isEqual(initialWorkflowDetailsConfiguration, workflowDetailsConfigurationState) &&
            isEqual(initialRpServiceConfigurations, rpServiceConfigurationsState)
        );
    }, [
        initialWorkflowDetailsConfiguration,
        workflowDetailsConfigurationState,
        initialRpServiceConfigurations,
        rpServiceConfigurationsState
    ]);

    useDiscardChangesModal({
        modalIdPrefix: "discard-workflow-changes",
        modalType: ModalTypes.DiscardWorkflowChanges,
        stateIsUnchanged
    });


    const handleSaveWithMessagingTemplatesUpdates = async () => {
        const messagingTemplatesToSave: Array<FormattedMessagingTemplate> = [];
        const messagingTemplatesToSavePerService: { [serviceId: string]: Array<FormattedMessagingTemplate> } = {};

        rpServiceConfigurationsState.forEach(serviceConfig => {
            //extract service level data to find the messaging templates we need to save
            const {
                availableMessagingTemplateTypes: currentAMTT,
                mergedEmailTemplateFields: currentMETF
            } = getAvailableMessagingTemplateTypesAndEmailTemplateFieldsFromRpServiceConfig(serviceConfig);

            const selectedMessagingTemplates = getSelectedMessagingTemplates(currentAMTT, currentMETF, workflowMessagingTemplateCache);

            //for each messaging template, add it to a service specific list and an aggregrate (unique) list
            selectedMessagingTemplates.forEach(mt => {
                if (messagingTemplatesToSavePerService[serviceConfig.id]) {
                    messagingTemplatesToSavePerService[serviceConfig.id].push(mt);
                } else if (!messagingTemplatesToSavePerService[serviceConfig.id]) {
                    messagingTemplatesToSavePerService[serviceConfig.id] = [mt];
                }

                if (messagingTemplatesToSave.findIndex(templateToSave => templateToSave.temporaryUUID === mt.temporaryUUID) < 0) {
                    messagingTemplatesToSave.push(mt);
                }
            });
        });

        //perform the messaging template updates
        const newMessagingTemplates = await updateOrCreateMessagingTemplates(dispatch)(messagingTemplatesToSave);
        const newFormattedMessagingTemplates = formatMessagingTemplates(newMessagingTemplates, axnCompanies, axnRelyingParties);

        //map each of the previously unsaved messaging templates (without id and with random UUID) to 
        //one of the newly saved messaging templates
        Object.keys(messagingTemplatesToSavePerService).forEach(serviceId => {
            messagingTemplatesToSavePerService[serviceId] = messagingTemplatesToSavePerService[serviceId].map((template) => {
                const index = messagingTemplatesToSave.findIndex(mt => mt.temporaryUUID === template.temporaryUUID);
                return newFormattedMessagingTemplates[index];
            });
        });

        //create the new messaging template cache
        const updatedMessagingTemplateCache: MessagingTemplateCache = {}
        newMessagingTemplates.forEach((template, index) => {
            updatedMessagingTemplateCache[template.uuid] = {
                messagingTemplate: template,
                formattedMessagingTemplate: newFormattedMessagingTemplates[index]
            };
        });

        const newRPServiceConfigurationState: Array<FormattedRpServiceConfiguration> = [];

        rpServiceConfigurationsState.forEach(serviceConfig => {
            const serviceMessagingTemplateUuids = (messagingTemplatesToSavePerService[serviceConfig.id] || []).map(mt => mt.temporaryUUID);
            //loop through the services and update their EMAIL_TEMPLATE_FIELDS properties as necessary
            const newMessagingTemplates = Object.keys(updatedMessagingTemplateCache)
                .map(templateUUID => updatedMessagingTemplateCache[templateUUID].formattedMessagingTemplate)
                .filter(template => serviceMessagingTemplateUuids.includes(template.temporaryUUID));

            const { mergedValue: mergedEmailTemplateFields } = getInheritedAndMergedJSONPropertyValue(
                serviceConfig.properties,
                serviceConfig.appliedProfiles,
                EMAIL_TEMPLATE_FIELDS_PROPERTY_TYPE_ID,
                {} as EmailTemplateFields
            );

            //extract email template fields from new data
            const newEmailTemplateFields = getEmailTemplateFieldsJSONValueFromMessagingTemplates(newMessagingTemplates);

            //clean out all old data stored for template/language files, email subjects and headerlogo
            //new data will be populated from the selectedMessagingTemplate list
            //only leaves the pinEmailFromAddress and dcuiEmailFromAddress
            const cleanedEmailTemplateFields = getCleanedEmailTemplateFields(mergedEmailTemplateFields);

            //merge the updated emailTemplateFields into the property
            const newProperties = serviceConfig.properties.map(property => {
                if (property.propertyTypeId === EMAIL_TEMPLATE_FIELDS_PROPERTY_TYPE_ID) {
                    //merge the new email template fields generated for the selected messaging templates
                    //with the cleaned data
                    return {
                        ...property,
                        value: JSON.stringify({
                            ...cleanedEmailTemplateFields,
                            ...newEmailTemplateFields
                        })
                    };
                }
                return property;
            });

            newRPServiceConfigurationState.push({
                ...serviceConfig,
                properties: newProperties
            });
        });

        setWorkflowMessagingTemplateCache(updatedMessagingTemplateCache);
        await handleSave(workflowDetailsConfigurationState, newRPServiceConfigurationState);
    };

    const activeRpServiceConfigurations = useMemo(() => {
        return rpServiceConfigurationsState.filter(service => service.status === EntityStatusTypes.ACTIVE);
    }, [rpServiceConfigurationsState]);

    const primaryRpService = useMemo(() => {
        if (workflowDetailsConfigurationState.rpServiceWorkflows) {
            const primaryRpServiceWorkflow = workflowDetailsConfigurationState.rpServiceWorkflows.find(rpsw => isStringTrue(rpsw.primaryServiceFlag));
            if (primaryRpServiceWorkflow) {
                return rpServiceConfigurationsState.find(rpsc => rpsc.id === primaryRpServiceWorkflow.rpAXNServiceID);
            }
        }
        return null;
    }, [workflowDetailsConfigurationState.rpServiceWorkflows, rpServiceConfigurationsState]);

    const sortedActiveRpServiceConfigurations = useMemo(() => {
        if (primaryRpService) {
            const sortedConfigs = [...activeRpServiceConfigurations];
            const primaryServiceIndex = sortedConfigs.findIndex(rps => rps.id === primaryRpService.id);
            if (primaryServiceIndex > 0) {
                const removedItems = sortedConfigs.splice(primaryServiceIndex, 1);
                sortedConfigs.unshift(...removedItems);
                return sortedConfigs;
            }
        }

        return activeRpServiceConfigurations
    }, [primaryRpService, activeRpServiceConfigurations]);



    useEffect(() => {
        if (shouldRefreshComponentState) {
            setWorkflowDetailsConfigurationState(initialWorkflowDetailsConfiguration);
        }
    }, [initialWorkflowDetailsConfiguration, shouldRefreshComponentState]);

    useEffect(() => {
        if (shouldRefreshComponentState) {
            setRpServiceConfigurationsState(initialRpServiceConfigurations);
        }
    }, [initialRpServiceConfigurations, shouldRefreshComponentState]);

    const onClickClose = () => {
        navigateToUtil(WORKFLOWS_URL, navigate, routerLocation.pathname);
    };

    const onClickSave = async () => {
        setRequestInProgress(true);
        await handleSaveWithMessagingTemplatesUpdates();
        setRequestInProgress(false);
    };

    const isUsingThemedUI = useMemo(() => {
        return workflowDetailsConfigurationState.appliedProfiles.some(appliedProfile => {
            const profileProperties = appliedProfile.profile.profileProperties;
            return !!profileProperties.find(property =>
                property.availableProperty?.id === USE_THEMED_UI_PROPERTY_TYPE_ID &&
                property.addedToProfile &&
                isStringTrue(property.value)
            );
        });
    }, [workflowDetailsConfigurationState.appliedProfiles]);

    const hasUnsubmittedChangeRequest = useMemo(() => {
        if (workflowChangeRequests.length > 0) {
            return workflowChangeRequests[0].changeRequestStatus === ChangeRequestStatusTypes.UNSUBMITTED;
        }
        return false;
    }, [workflowChangeRequests]);

    const simpleRPServiceWorkflows = useMemo(() => {
        return hasUnsubmittedChangeRequest ? axnAdminRPServiceWorkflows : axnRPServiceWorkflows;
    }, [hasUnsubmittedChangeRequest, axnRPServiceWorkflows, axnAdminRPServiceWorkflows]);

    const isUIDisabled = useMemo(() => {
        return !workflowDetailsConfigurationState.permissions.canModify || !hasUnsubmittedChangeRequest || requestInProgress;
    }, [workflowDetailsConfigurationState.permissions.canModify, hasUnsubmittedChangeRequest, requestInProgress]);

    const mergedAXNUITheme: AXNUITheme = useMemo(() => {
        const axnuiThemeProfileProperties = getProfilePropertyValuesForPropertyTypeId(workflowDetailsConfigurationState.appliedProfiles, AXNUI_THEME_PROPERTY_TYPE_ID);
        if (axnuiThemeProfileProperties.length > 0) {
            return merge({}, DEFAULT_AXNUI_THEME, ...axnuiThemeProfileProperties);
        }
        return DEFAULT_AXNUI_THEME;
    }, [workflowDetailsConfigurationState.appliedProfiles]);

    const mergedAXNUILayout: AXNUILayout = useMemo(() => {
        const axnuiLayoutProfileProperties = getProfilePropertyValuesForPropertyTypeId(workflowDetailsConfigurationState.appliedProfiles, AXNUI_LAYOUT_PROPERTY_TYPE_ID);
        if (axnuiLayoutProfileProperties.length > 0) {
            return merge({}, DEFAULT_AXNUI_LAYOUT, ...axnuiLayoutProfileProperties);
        }
        return DEFAULT_AXNUI_LAYOUT;
    }, [workflowDetailsConfigurationState.appliedProfiles]);

    const {
        mergedContent: mergedAXNUIContent,
        multiLanguageContent: multiLanguageAXNUIContent,
        availableLanguages: availableAXNUILanguages
    } = useMemo(() => {
        return extractContentValuesFromProfileProperties(
            workflowDetailsConfigurationState.appliedProfiles,
            AXNUI_CONTENT_PROPERTY_TYPE_ID,
            DEFAULT_AXNUI_CONTENT,
            selectedLanguage
        );
    }, [workflowDetailsConfigurationState.appliedProfiles, selectedLanguage])

    const mergedDCUITheme: DCUITheme = useMemo(() => {
        const dcuiThemeProfileProperties = getProfilePropertyValuesForPropertyTypeId(workflowDetailsConfigurationState.appliedProfiles, DCUI_THEME_PROPERTY_TYPE_ID);
        if (dcuiThemeProfileProperties.length > 0) {
            return merge({}, DEFAULT_DCUI_THEME, ...dcuiThemeProfileProperties);
        }
        return DEFAULT_DCUI_THEME;
    }, [workflowDetailsConfigurationState.appliedProfiles]);

    const {
        mergedContent: mergedDCUIContent,
        multiLanguageContent: multiLanguageDCUIContent,
        availableLanguages: availableDCUILanguages
    } = useMemo(() => {
        return extractContentValuesFromProfileProperties(
            workflowDetailsConfigurationState.appliedProfiles,
            DCUI_CONTENT_PROPERTY_TYPE_ID,
            DEFAULT_DCUI_CONTENT,
            selectedLanguage
        );
    }, [workflowDetailsConfigurationState.appliedProfiles, selectedLanguage])

    const workflowProfile = useMemo(() => {
        if (workflowDetailsConfigurationState.appliedProfiles?.length > 0) {
            //todo - when variants become a thing, this is where we would select the right workflow profile
            const appliedProfile = workflowDetailsConfigurationState.appliedProfiles.find(ap => ap.type === AppliedProfileTypes.WORKFLOW);
            if (appliedProfile) {
                return appliedProfile.profile;
            }
        }
        return null;
    }, [workflowDetailsConfigurationState.appliedProfiles]);

    const customLanguages: Array<LocaleTypes> = useMemo(() => {
        if (workflowProfile) {
            return getCustomLanguages(PageLevelTypes.WORKFLOW, (workflowProfile.profileProperties || []));
        }

        return [];

    }, [workflowProfile.profileProperties]);

    useEffect(() => {
        if (customLanguages.length === 0 && availableAXNUILanguages && availableDCUILanguages) {
            const combinedLanguages = Array.from(new Set([...availableAXNUILanguages, ...availableDCUILanguages]));

            if (combinedLanguages.length > 0) {
                if (!combinedLanguages.includes(selectedLanguage)) {
                    setSelectedLanguage(combinedLanguages[0]);
                }

                setIsMultiLanguageEnabled(true);
                setAvailableLanguages(combinedLanguages);
            } else if (selectedLanguage) {
                setSelectedLanguage(selectedLanguage);
                setAvailableLanguages([]);
            } else {
                setSelectedLanguage(LocaleTypes.ENGLISH);
                setAvailableLanguages([]);
            }
        } else if (customLanguages.length > 0) {
            setIsMultiLanguageEnabled(true);
            setAvailableLanguages(customLanguages);
        }
    }, [
        customLanguages,
        selectedLanguage,
        availableAXNUILanguages,
        availableDCUILanguages
    ]);

    // Update the theming content with multi language content if the theming layout property isLanguageSelectorEnabled changes
    const handleUpdateRpServicesAXNUIAndDCUIContentProperty = (availableLanguages: LocaleTypes[], isMultiLanguageEnabled: boolean) => {
        const updatedRpServiceConfigurations = updateRpServicesAXNUIAndDCUIContentProperty(
            isMultiLanguageEnabled,
            rpServiceConfigurationsState,
            multiLanguageAXNUIContent,
            multiLanguageDCUIContent,
            availableLanguages,
            selectedLanguage
        );

        setRpServiceConfigurationsState(updatedRpServiceConfigurations);
    };

    const setWorkflowProfile = (newWorkflowProfile: FormattedIdwProfile) => {
        const newAppliedProfiles = workflowDetailsConfigurationState.appliedProfiles.map(ap => {
            if (ap.profile.id === newWorkflowProfile.id) {
                return {
                    ...ap,
                    profile: newWorkflowProfile
                }
            }
            return ap;
        });

        const newRpServices = rpServiceConfigurationsState.map(rps => {
            const newRPS = rps;

            if (rps.appliedProfiles && rps.appliedProfiles.length > 0) {
                newRPS.appliedProfiles = rps.appliedProfiles.map(ap => {
                    if (ap.profile.id === newWorkflowProfile.id) {
                        return {
                            ...ap,
                            profile: newWorkflowProfile
                        };
                    }
                    return ap;
                })
            }

            return newRPS;
        });

        setWorkflowDetailsConfigurationState({
            ...workflowDetailsConfigurationState,
            appliedProfiles: newAppliedProfiles
        });

        setRpServiceConfigurationsState(newRpServices);
    };

    const handleChangeWorkflowAttributeLabel = (attributeTypeName: AttributeTypes, value: string) => {
        const newProperties = workflowProfile.profileProperties.map(property => {
            if (property.availableProperty?.id === AXNUI_CONTENT_PROPERTY_TYPE_ID) {
                const content = getParsedContentPropertyValue<AXNUIContent>(isMultiLanguageEnabled, multiLanguageAXNUIContent, property, selectedLanguage);


                const newAXNUIContent: AXNUIContent = {
                    ...content,
                    labels: {
                        ...content?.labels || {},
                        attributes: {
                            ...content?.labels?.attributes,
                            [attributeTypeName]: value
                        }
                    }
                };

                return generateContentProfileProperty(property, isMultiLanguageEnabled, multiLanguageAXNUIContent, availableLanguages, selectedLanguage, newAXNUIContent, DEFAULT_AXNUI_CONTENT);
            }

            return property;
        });

        setWorkflowProfile({
            ...workflowProfile,
            profileProperties: newProperties
        });
    }

    const handleChangeWorkflowAttributeForm = (attributeTypeName: AttributeTypes, key: keyof AXNUIContentForm, value: string) => {
        const newProperties = workflowProfile.profileProperties.map(property => {
            if (property.availableProperty?.id === AXNUI_CONTENT_PROPERTY_TYPE_ID) {
                const content = getParsedContentPropertyValue<AXNUIContent>(isMultiLanguageEnabled, multiLanguageAXNUIContent, property, selectedLanguage);

                const newAXNUIContent = {
                    ...content,
                    form: getAXNUIFormContentKey(attributeTypeName, key, value, content?.form)
                }

                return generateContentProfileProperty(property, isMultiLanguageEnabled, multiLanguageAXNUIContent, availableLanguages, selectedLanguage, newAXNUIContent, DEFAULT_AXNUI_CONTENT);
            }

            return property;
        });

        setWorkflowProfile({
            ...workflowProfile,
            profileProperties: newProperties
        });
    }

    const formattedRpServiceAttributes = useMemo(() => {
        const addedAttributesIds: Array<string> = [];
        return activeRpServiceConfigurations.reduce((attributes, rpServiceConfiguration) => {
            const activeAttributes = rpServiceConfiguration.attributes.filter(attr => attr.addedToService && attr.status === EntityStatusTypes.ACTIVE);
            attributes = attributes.concat(activeAttributes);
            return attributes;
        }, []).filter(activeAttr => {
            if (!addedAttributesIds.includes(activeAttr.attributeType.id)) {
                addedAttributesIds.push(activeAttr.attributeType.id);
                return true;
            }

            return false;
        });
    }, [activeRpServiceConfigurations]);

    const documentCaptureSteps: Array<DCUIThemingStep> = useMemo(() => {
        return getActiveWorkflowDCUICaptureSteps(activeRpServiceConfigurations);
    }, [activeRpServiceConfigurations]);

    const axnAttributeTypeFieldMap = useMemo(() => {
        if (axnAttributeTypeFields.length) {
            return getAxnAttributeTypeFieldMapWithoutSelfServiceCheck(axnAttributeTypeFields)
        }

        return {};
    }, [axnAttributeTypeFields]);

    const workflowProfileKey = `w${workflowDetailsConfigurationState.id}`;

    const {
        profileRedirectURLsWithKey
    } = useMemo(() => {
        return getAppliedProfileRedirectURLs(workflowDetailsConfigurationState.appliedProfiles, workflowProfileKey, true);
    }, [workflowDetailsConfigurationState.appliedProfiles]);

    const primaryRpServicePropertyRedirectURLs = useMemo(() => {
        if (primaryRpService) {
            return getFormattedRpServiceConfigurationPropertyRedirectURLs(
                primaryRpService.properties,
                "",
                false);
        }

        return [];

    }, [primaryRpService]);

    const isDisplayResultsEnabled = useMemo(() => {
        const displayResultsValue = getPropertyValuesPassedToService(
            workflowDetailsConfigurationState.appliedProfiles,
            DISPLAY_RESULTS_PROPERTY_TYPE_ID
        );
        return isStringTrue(displayResultsValue);
    }, [workflowDetailsConfigurationState.appliedProfiles]);

    const showIncodeWidget = useMemo(() => {
        return !!activeRpServiceConfigurations.some(rpServiceConfigurationState => rpServiceConfigurationState.serviceOfferings.find(so =>
            so.addedToService &&
            so.status === EntityStatusTypes.ACTIVE &&
            so.serviceOfferingID === INCODE_BIOGOVID_VERIFICATION_SERVICE_OFFERING_ID
        ));
    }, [activeRpServiceConfigurations]);

    const validationErrors = useMemo(() => {
        return getWorkflowValidationErrors(
            true,
            workflowDetailsConfigurationState.hasDCUIService,
            documentCaptureSteps,
            mergedAXNUIContent,
            mergedAXNUILayout,
            mergedAXNUITheme,
            mergedDCUIContent,
            mergedDCUITheme,
            workflowDetailsConfigurationState,
            formattedRpServiceAttributes,
            isMultiLanguageEnabled,
            availableLanguages,
            isDisplayResultsEnabled,
            profileRedirectURLsWithKey,
            primaryRpService
        );
    }, [
        isUsingThemedUI,
        documentCaptureSteps,
        mergedAXNUIContent,
        mergedAXNUILayout,
        mergedAXNUITheme,
        mergedDCUIContent,
        mergedDCUITheme,
        workflowDetailsConfigurationState,
        formattedRpServiceAttributes,
        isMultiLanguageEnabled,
        availableLanguages,
        isDisplayResultsEnabled,
        profileRedirectURLsWithKey,
        primaryRpService
    ]);

    const updateProperties = (properties: Array<{ propertyTypeId: string, newValue: string, added: boolean }>) => {
        const newProperties = workflowProfile.profileProperties.map(property => {
            const foundIndex = properties.findIndex(passedProperty => passedProperty.propertyTypeId === property.availableProperty?.id);
            if (foundIndex > -1) {
                return setValueAndAddedToProfileForProfileProperty(property, properties[foundIndex].newValue, properties[foundIndex].added);
            }

            return property;
        });

        setWorkflowProfile({
            ...workflowProfile,
            profileProperties: newProperties
        });
    };


    const uploadFileToS3 = async (file: File, category: string): Promise<string> => {
        const workflowId = workflowDetailsConfigurationState.id;
        const fileTypeStringArray = file.type.split("/");
        const fileType = fileTypeStringArray[fileTypeStringArray.length - 1];
        const formattedDate = format(new Date(), SERVER_TIME_FORMAT);
        const fileName = `${category}-w${workflowId}-${formattedDate}`;
        const base64Data = await convertFileToBase64(file);

        if (base64Data) {
            const { data, error } = await uploadResourceRequest(base64Data, fileType, fileName, workflowId, null, category);
            if (data?.url) {
                return data.url;
            } else {
                if (error) {
                    logAndDisplayErrorModal(
                        dispatch,
                        error,
                        resources.requests.uploadResource.error.title,
                        resources.requests.uploadResource.error.description,
                    );
                }
            }
        }
        return null;
    };

    const handleSetAsPrimaryService = (serviceId: string) => {
        const { rpServiceWorkflows } = workflowDetailsConfigurationState;
        const updatedRpServiceWorkflows = rpServiceWorkflows.map(rpsw => {
            if (isStringTrue(rpsw.primaryServiceFlag)) {
                return { ...rpsw, primaryServiceFlag: `${false}` };
            }

            if (rpsw.rpAXNServiceID === serviceId) {
                return { ...rpsw, primaryServiceFlag: `${true}` };
            }

            return rpsw;
        });

        setWorkflowDetailsConfigurationState({
            ...workflowDetailsConfigurationState,
            rpServiceWorkflows: updatedRpServiceWorkflows
        });
    }

    const handleAddServiceToArchive = (serviceId: string) => {
        // archive if there is no active change requests
        const updatedServices = rpServiceConfigurationsState.map(rpService => {
            if (rpService.id === serviceId) {
                return { ...rpService, status: EntityStatusTypes.INACTIVE };
            }
            return rpService;
        });

        handleUpdateRpServiceConfigurationsStateAndRpServices(updatedServices);
    }

    const redirectUrlsInformation = useMemo(() => {
        let redirectUrls: Array<BaseRedirectUrl> = [
            ...profileRedirectURLsWithKey,
            ...primaryRpServicePropertyRedirectURLs
        ];

        if (primaryRpService) {
            if (primaryRpService.callBackURL) {
                redirectUrls.push({
                    index: 0,
                    url: primaryRpService.callBackURL,
                    isEditable: true,
                    redirectUrlOrigin: RedirectUrlOriginTypes.CALLBACK_URL
                })
            }

            if (primaryRpService.redirects) {
                redirectUrls = [
                    ...redirectUrls,
                    ...primaryRpService.redirects.map((redirect, index) => ({ index, url: redirect.redirectURI, isEditable: false, redirectUrlOrigin: RedirectUrlOriginTypes.REDIRECT }))
                ];
            }
        }

        return getRedirectUrlsInformation(redirectUrls, true);
    }, [primaryRpServicePropertyRedirectURLs, profileRedirectURLsWithKey, primaryRpService]);

    const generateStartChangeRequestButton = (
        additionalSetWorkflowChangeRequest: (changeRequests: Array<WorkflowChangeRequest>) => void
    ) => {
        return (
            <WorkflowDetailsStartChangeRequestButton
                changeRequestStatus={null}
                workflowDetailsConfiguration={workflowDetailsConfigurationState}
                setWorkflowChangeRequest={setWorkflowChangeRequest}
                additionalSetWorkflowChangeRequests={additionalSetWorkflowChangeRequest}
            />
        );
    }


    const getAllRpServiceConfigurations = (updatedWorkflowRpServices: Array<FormattedRpServiceConfiguration>, oldRpServiceConfigurationsState: Array<FormattedRpServiceConfiguration>) => {
        return oldRpServiceConfigurationsState.map(rpService => {
            const updatedRpService = updatedWorkflowRpServices.find(workflowRpService => workflowRpService.id === rpService.id);

            if (updatedRpService) {
                return updatedRpService;
            } else {
                return rpService
            }
        });
    }

    const handleUpdateRpServiceConfigurationsStateAndRpServices = (workflowRpServices: Array<FormattedRpServiceConfiguration>) => {
        const fullRpServices = getAllRpServiceConfigurations(workflowRpServices, rpServiceConfigurationsState);

        setRpServiceConfigurationsState(fullRpServices);

        const { hasAtLeastOneRpServiceUpdated, updatedRpServices } = getUpdatedAxnRpServices(fullRpServices, axnRPServices);

        if (hasAtLeastOneRpServiceUpdated) {
            dispatch(setPartialCommonReferenceData({ axnRPServices: updatedRpServices }, CommonReferenceDataPageTypes.WORKFLOW_DETAILS));
        }
    }

    const handleUpdateRpServiceConfigurationsState = (workflowRpServices: Array<FormattedRpServiceConfiguration>) => {
        const fullRpServices = getAllRpServiceConfigurations(workflowRpServices, rpServiceConfigurationsState);
        setRpServiceConfigurationsState(fullRpServices);
    }

    return (
        <div className={styles.workflowDetailsComponent}>
            <div className={styles.headerContainer}>
                <div className={styles.headerRow}>
                    <span className={styles.workflowName}>{workflowDetailsConfigurationState.name}</span>
                    {workflowChangeRequests.length > 0 && (
                        <Badge pill bg={"secondary"}>{workflowChangeRequests[0].changeRequestStatus}</Badge>
                    )}
                    <div className={styles.buttonContainer}>
                        <Button id="workflow-details-close-button" variant={"outline-secondary"} onClick={onClickClose}>{resources.common.buttons.close.label}</Button>

                        {hasUnsubmittedChangeRequest && workflowDetailsConfigurationState.permissions.canModify && (
                            <ButtonWithSpinner
                                id="workflow-details-save-button"
                                disabled={stateIsUnchanged || requestInProgress || validationErrors.count > 0}
                                hideSpinner={stateIsUnchanged || validationErrors.count > 0}
                                label={resources.common.buttons.save.label}
                                type={ButtonTypes.SUBMIT}
                                onClick={onClickSave}
                                variant={"primary"}
                            />
                        )}
                        <WorkflowDetailsDropdownMenu
                            stateIsUnchanged={stateIsUnchanged}
                            validationErrors={validationErrors}
                            initialWorkflowDetailsConfiguration={initialWorkflowDetailsConfiguration}
                            workflowDetailsConfiguration={workflowDetailsConfigurationState}
                            workflowChangeRequests={workflowChangeRequests}
                            userPermissions={userPermissions}
                            initialRpServiceConfigurations={initialRpServiceConfigurations}
                            rpServiceConfigurations={rpServiceConfigurationsState}
                            rpServiceTemplates={rpServiceTemplates}
                            axnRPServices={axnRPServices}
                            simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                            setWorkflowDetailsConfigurationState={setWorkflowDetailsConfigurationState}
                            setRpServiceConfigurationsState={setRpServiceConfigurationsState}
                            setWorkflowChangeRequest={setWorkflowChangeRequest}
                            getFormattedRpServiceForNewService={getFormattedRpServiceForNewService}
                        />
                    </div>
                </div>
            </div>
            {!!workflowProfile && !!primaryRpService && (
                <TabContainer>
                    {/* @ts-ignore */}
                    <Tabs id="workflow" fill={false} unmountOnExit={true}>
                        <Tab eventKey="general" title={validationErrors.general.tabTitle}>
                            <WorkflowDetailsGeneralTab
                                axnDocumentationLinks={axnDocumentationLinks}
                                isDisabled={isUIDisabled}
                                validationErrors={validationErrors.general}
                                axnCompanies={axnCompanies}
                                axnRelyingParties={axnRelyingParties}
                                primaryRpService={primaryRpService}
                                rpServiceConfigurations={sortedActiveRpServiceConfigurations}
                                workflowProfile={workflowProfile}
                                workflowDetailsConfigurationState={workflowDetailsConfigurationState}
                                multiLanguageAXNUIContent={multiLanguageAXNUIContent}
                                multiLanguageDCUIContent={multiLanguageDCUIContent}
                                axnuiLayout={mergedAXNUILayout}
                                availableLanguages={availableLanguages}
                                isMultiLanguageEnabled={isMultiLanguageEnabled}
                                workflowProfileKey={workflowProfileKey}
                                showIncodeWidget={showIncodeWidget}
                                redirectUrlsInformation={redirectUrlsInformation}
                                setAvailableLanguages={setAvailableLanguages}
                                setWorkflowProfile={setWorkflowProfile}
                                setRpServiceConfigurationsState={handleUpdateRpServiceConfigurationsState}
                                setWorkflowDetailsConfigurationState={setWorkflowDetailsConfigurationState}
                                setIsMultiLanguageEnabled={setIsMultiLanguageEnabled}
                                updateProperties={updateProperties}
                                updateRpServicesAXNUIAndDCUIContentProperty={handleUpdateRpServicesAXNUIAndDCUIContentProperty}
                            />
                        </Tab>
                        <Tab eventKey="visualization" title={resources.pages.workflowDetails.tabs.visualization.tabName}>
                            <WorkflowDetailsVisualizationTab
                                hasUnsubmittedChangeRequest={hasUnsubmittedChangeRequest}
                                rpServices={sortedActiveRpServiceConfigurations}
                                workflowMessagingTemplateCache={workflowMessagingTemplateCache}
                                selectedLanguage={selectedLanguage}
                                availableLanguages={availableLanguages}
                                primaryRpServiceId={primaryRpService.id}
                                generateStartChangeRequestButton={generateStartChangeRequestButton}
                                workflowChangeRequests={workflowChangeRequests}
                                setRpServiceConfigurationsState={handleUpdateRpServiceConfigurationsStateAndRpServices}
                                setWorkflowMessagingTemplateCache={setWorkflowMessagingTemplateCache}
                                onClickArchiveService={handleAddServiceToArchive}
                                onClickSetAsPrimaryService={handleSetAsPrimaryService}
                            />
                        </Tab>
                        { /* todo - should be checking for isUsingThemedUI here */}
                        {(
                            workflowDetailsConfigurationState.permissions.canViewBranding &&
                            workflowDetailsConfigurationState.permissions.canViewAXNUICustomization
                        ) && (
                                <Tab eventKey="theming" title={validationErrors.theming.tabTitle}>
                                    <WorkflowDetailsThemingTab
                                        isDisabled={isUIDisabled}
                                        selectedLanguage={selectedLanguage}
                                        availableLanguages={availableLanguages}
                                        isMultiLanguageEnabled={isMultiLanguageEnabled}
                                        multiLanguageAXNUIContent={multiLanguageAXNUIContent}
                                        validationErrors={validationErrors.theming}
                                        inheritedAXNUIContent={DEFAULT_AXNUI_CONTENT[selectedLanguage]}
                                        axnuiContent={mergedAXNUIContent}
                                        axnuiLayout={mergedAXNUILayout}
                                        axnuiTheme={mergedAXNUITheme}
                                        customLanguages={customLanguages}
                                        redirectUrlsInformation={redirectUrlsInformation}
                                        workflowProfile={workflowProfile}
                                        isDisplayResultsEnabled={isDisplayResultsEnabled}
                                        workflowDetailsConfigurationState={workflowDetailsConfigurationState}
                                        workflowProfileKey={workflowProfileKey}
                                        primaryRpService={primaryRpService}
                                        rpServiceConfigurations={rpServiceConfigurationsState}
                                        setRpServiceConfigurationsState={setRpServiceConfigurationsState}
                                        updateProperties={updateProperties}
                                        setWorkflowProfile={setWorkflowProfile}
                                        uploadFileToS3={uploadFileToS3}
                                    />
                                </Tab>
                            )}
                        {(
                            workflowDetailsConfigurationState.permissions.canViewBranding &&
                            workflowDetailsConfigurationState.permissions.canViewDCUICustomization &&
                            workflowDetailsConfigurationState.hasDCUIService
                        ) && (
                                <Tab eventKey="document-capture" title={validationErrors.dcuiTheming.tabTitle}>
                                    <WorkflowDetailsDCUIThemingTab
                                        isDisabled={isUIDisabled}
                                        selectedLanguage={selectedLanguage}
                                        availableLanguages={availableLanguages}
                                        isMultiLanguageEnabled={isMultiLanguageEnabled}
                                        documentCaptureSteps={documentCaptureSteps}
                                        multiLanguageDCUIContent={multiLanguageDCUIContent}
                                        validationErrors={validationErrors.dcuiTheming}
                                        inheritedDCUIContent={DEFAULT_DCUI_CONTENT[selectedLanguage]}
                                        dcuiContent={mergedDCUIContent}
                                        dcuiTheme={mergedDCUITheme}
                                        workflowProfile={workflowProfile}
                                        workflowDetailsConfigurationState={workflowDetailsConfigurationState}
                                        setWorkflowProfile={setWorkflowProfile}
                                        uploadFileToS3={uploadFileToS3}
                                    />
                                </Tab>
                            )}
                        <Tab eventKey="attributes" title={validationErrors.attributes.tabTitle}>
                            <WorkflowDetailsAttributesTab
                                isDisabled={isUIDisabled}
                                axnuiContent={mergedAXNUIContent}
                                formattedRpServiceAttributes={formattedRpServiceAttributes}
                                validationErrors={validationErrors.attributes}
                                axnAttributeTypeFieldMap={axnAttributeTypeFieldMap}
                                attributeLabels={attributeLabels}
                                setAttributeLabel={handleChangeWorkflowAttributeLabel}
                                setAttributeForm={handleChangeWorkflowAttributeForm}
                            />
                        </Tab>
                    </Tabs>
                </TabContainer>
            )}
            {!workflowProfile && (
                <Alert>No workflow profiles have been added yet. This is a configuration issue.</Alert>
            )}
            {!primaryRpService && (
                <Alert>This workflow does not have a primary service. This is a configuration issue.</Alert>
            )}
        </div>
    );
};